import mqtt from "mqtt";
import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook para recibir posiciones GPS en tiempo real via MQTT.
 * Suscribe a myindustrialdatagps/{projectId} y mantiene un mapa de posiciones por device_user_id.
 * @param {number} projectId - ID del proyecto
 * @returns {object} - { positions, loaded, reconnecting }
 */
export default function useMQTTGPS(projectId) {
    const [client, setClient] = useState(null);
    const [reconnecting, setReconnecting] = useState(false);
    const [loaded, setLoaded] = useState(false);
    // positions: { device_user_id: { latitude, longitude, battery, device_name, last_message, is_sos } }
    const [positions, setPositions] = useState({});
    const subscribedTopics = useRef(new Set());

    useEffect(() => {
        if (!projectId) return;

        const brokerUrl = process.env.REACT_APP_MQTT_WS_URL;
        const username = process.env.REACT_APP_MQTT_USER;
        const password = process.env.REACT_APP_MQTT_PASSWORD;

        if (!brokerUrl) return;

        const mqttClient = mqtt.connect(brokerUrl, {
            username,
            password,
            clientId: `web_gps_${Math.random().toString(16).substring(2, 10)}`,
            clean: true,
            reconnectPeriod: 5000,
            connectTimeout: 30000,
        });

        setClient(mqttClient);

        return () => {
            if (mqttClient) mqttClient.end();
        };
    }, [projectId]);

    useEffect(() => {
        if (!client || !projectId) return;

        const topic = `myindustrialdatagps/${projectId}`;

        const onConnect = () => {
            setReconnecting(false);
            setLoaded(true);

            if (!subscribedTopics.current.has(topic)) {
                client.subscribe(topic, { qos: 1 }, (err) => {
                    if (!err) subscribedTopics.current.add(topic);
                });
            }
        };

        const onMessage = (receivedTopic, message) => {
            if (!receivedTopic.startsWith('myindustrialdatagps/')) return;

            try {
                const parsed = JSON.parse(message.toString());
                const deviceUserId = parsed.device_user_id;
                if (deviceUserId) {
                    setPositions(prev => ({
                        ...prev,
                        [deviceUserId]: {
                            latitude: parsed.latitude,
                            longitude: parsed.longitude,
                            battery: parsed.battery,
                            device_name: parsed.device_name,
                            last_message: parsed.last_message,
                            is_sos: parsed.is_sos || false,
                        }
                    }));
                }
            } catch (e) {
                console.error('MQTT GPS: Error parsing message', e);
            }
        };

        const onError = () => setReconnecting(true);
        const onClose = () => setReconnecting(true);
        const onReconnect = () => {
            setReconnecting(true);
            subscribedTopics.current.clear();
        };

        client.on('connect', onConnect);
        client.on('message', onMessage);
        client.on('error', onError);
        client.on('close', onClose);
        client.on('reconnect', onReconnect);

        if (client.connected) onConnect();

        return () => {
            client.off('connect', onConnect);
            client.off('message', onMessage);
            client.off('error', onError);
            client.off('close', onClose);
            client.off('reconnect', onReconnect);

            subscribedTopics.current.forEach(t => client.unsubscribe(t));
            subscribedTopics.current.clear();
        };
    }, [client, projectId]);

    const getPosition = useCallback((deviceUserId) => {
        return positions[deviceUserId] || null;
    }, [positions]);

    return { positions, loaded, reconnecting, getPosition };
}
